import { sizes } from "@/styles/breakpoints";
import { HotelImageEntry } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";

const IMAGEKIT_FETCH_BASE_URL = "https://media.staticroot.com/image/fetch";

/**
 * Sort hotel image entries by their order field (ascending).
 * Shared between server and client components to ensure consistent ordering.
 */
export const sortImagesByOrder = (entries: HotelImageEntry[]): HotelImageEntry[] =>
  [...entries].sort((a, b) => a.image.order - b.image.order);

/**
 * Core ImageKit URL builder with crop-to-fill mode (c-maintain_ratio_no_enlarge).
 * Use for images that need to fill a container while cropping excess.
 */
export const getOptimizedImageUrl = (
  url: string,
  width: number,
  height: number
): string => {
  const params = [
    "f-auto",
    "fo-auto",
    "q-70",
    "c-maintain_ratio_no_enlarge",
    `w-${width}`,
    `h-${height}`,
  ].join(",");

  return `${IMAGEKIT_FETCH_BASE_URL}/${encodeURIComponent(url)}?tr=${params}`;
};

/**
 * ImageKit URL builder with contain mode (c-at_max).
 * Use for images that need to fit within bounds without cropping.
 */
export const getContainedImageUrl = (url: string, maxWidth: number): string => {
  const params = ["f-auto", "q-70", "c-at_max", `w-${maxWidth}`].join(",");

  return `${IMAGEKIT_FETCH_BASE_URL}/${encodeURIComponent(url)}?tr=${params}`;
};

/**
 * srcSet and sizes helpers for FullGalleryDialog grid.
 * Grid has full-width (2-column span) and half-width (1-column) images.
 */
export const fullGallery = {
  /** Full-width image srcSet (spanning 2 columns, max 800px x 532px) */
  getFullSizeSrcSet: (url: string): string =>
    [
      `${getOptimizedImageUrl(url, 407, 271)} 407w`,
      `${getOptimizedImageUrl(url, 749, 499)} 749w`,
      `${getOptimizedImageUrl(url, 800, 532)} 800w`,
      `${getOptimizedImageUrl(url, 1600, 1064)} 1600w`,
    ].join(", "),

  /** Half-width image srcSet (single column, max 400px x 266px) */
  getHalfSizeSrcSet: (url: string): string =>
    [
      `${getOptimizedImageUrl(url, 203, 135)} 203w`,
      `${getOptimizedImageUrl(url, 374, 249)} 374w`,
      `${getOptimizedImageUrl(url, 400, 266)} 400w`,
      `${getOptimizedImageUrl(url, 800, 532)} 800w`,
    ].join(", "),

  getFullSizeSizes: (): string =>
    `(min-width: ${sizes.screenMdMin}px) 800px, (min-width: ${sizes.screenSmMin}px) 749px, 100vw`,

  getHalfSizeSizes: (): string =>
    `(min-width: ${sizes.screenMdMin}px) 400px, (min-width: ${sizes.screenSmMin}px) 374px, 50vw`,
};

/**
 * srcSet and sizes helpers for ImageGalleryClient preview grid.
 * Preview has a main image (2x2 grid area) and 4 smaller images.
 */
export const galleryPreview = {
  getSrcSet: (url: string, isMain: boolean): string => {
    if (isMain) {
      return [
        `${getOptimizedImageUrl(url, 290, 300)} 290w`,
        `${getOptimizedImageUrl(url, 340, 300)} 340w`,
        `${getOptimizedImageUrl(url, 351, 264)} 351w`,
        `${getOptimizedImageUrl(url, 461, 264)} 461w`,
        `${getOptimizedImageUrl(url, 618, 400)} 476w`,
        // 2x retina
        `${getOptimizedImageUrl(url, 580, 600)} 580w`,
        `${getOptimizedImageUrl(url, 680, 600)} 680w`,
        `${getOptimizedImageUrl(url, 702, 528)} 702w`,
        `${getOptimizedImageUrl(url, 922, 528)} 922w`,
        `${getOptimizedImageUrl(url, 1236, 800)} 952w`,
      ].join(", ");
    }

    return [
      `${getOptimizedImageUrl(url, 290, 300)} 290w`,
      `${getOptimizedImageUrl(url, 340, 300)} 340w`,
      `${getOptimizedImageUrl(url, 176, 128)} 176w`,
      `${getOptimizedImageUrl(url, 231, 128)} 231w`,
      `${getOptimizedImageUrl(url, 288, 196)} 288w`,
      // 2x retina
      `${getOptimizedImageUrl(url, 580, 600)} 580w`,
      `${getOptimizedImageUrl(url, 680, 600)} 680w`,
      `${getOptimizedImageUrl(url, 352, 256)} 352w`,
      `${getOptimizedImageUrl(url, 462, 256)} 462w`,
      `${getOptimizedImageUrl(url, 576, 392)} 576w`,
    ].join(", ");
  },

  getSizes: (isMain: boolean): string => {
    if (isMain) {
      return [
        "(min-width: 1200px) 476px",
        "(min-width: 992px) 461px",
        "(min-width: 768px) 351px",
        "(min-width: 480px) 340px",
        "290px",
      ].join(", ");
    }

    return [
      "(min-width: 1200px) 288px",
      "(min-width: 992px) 231px",
      "(min-width: 768px) 176px",
      "(min-width: 480px) 340px",
      "290px",
    ].join(", ");
  },
};

/**
 * srcSet and sizes helpers for HotelListCard ImageCarousel.
 */
export const hotelListCard = {
  getSrcSet: (url: string): string =>
    [
      `${getOptimizedImageUrl(url, 407, 335)} 407w`, // Mobile
      `${getOptimizedImageUrl(url, 749, 617)} 749w`, // Tablet
      `${getOptimizedImageUrl(url, 306, 250)} 306w`, // Desktop
      `${getOptimizedImageUrl(url, 814, 670)} 814w`, // Mobile 2x
      `${getOptimizedImageUrl(url, 1498, 1234)} 1498w`, // Tablet 2x
      `${getOptimizedImageUrl(url, 612, 500)} 612w`, // Desktop 2x
    ].join(", "),

  sizes: "(max-width: 425px) 407px, (max-width: 767px) 749px, 286px",
};
